import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

const PureSoulsLogin = () => {
  const { login, AUTHORIZED_STAFF_EMAILS } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    script.onload = () => {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: false,
        });
        window.google.accounts.id.renderButton(
          document.getElementById('google-signin-button'),
          { theme: 'outline', size: 'large', type: 'standard', shape: 'rectangular', text: 'signin_with', logo_alignment: 'left', width: 350 }
        );
      }
    };

    return () => {
      if (document.head.contains(script)) document.head.removeChild(script);
    };
  }, []);

  const handleGoogleResponse = (response) => {
    setLoading(true);
    setError('');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential: response.credential })
    })
      .then(res => res.json())
      .then(data => {
        if (data.session_token) {
          // Store session token for apiClient
          localStorage.setItem('sessionToken', data.session_token);
          // Decode Google JWT to get user info for AuthContext
          const payload = JSON.parse(atob(response.credential.split('.')[1]));
          const userData = {
            email: data.user_email,
            name: data.user_name,
            picture: data.user_picture,
            session_token: data.session_token,
            domain: payload.hd
          };
          const success = login(userData);
          if (!success) setError('Your email is not authorized for CRM access.');
        } else if (data.detail) {
          setError(data.detail);
        } else {
          setError('Login failed. Please try again.');
        }
      })
      .catch(() => setError('Login failed. Please try again.'))
      .finally(() => setLoading(false));
  };

  const [demoPassword, setDemoPassword] = useState('');

  const handleDemoLogin = () => {
    if (!demoPassword) {
      setError('Enter the demo access password.');
      return;
    }
    setLoading(true);
    setError('');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/demo-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: demoPassword })
    })
      .then(res => res.json())
      .then(data => {
        if (data.session_token) {
          localStorage.setItem('sessionToken', data.session_token);
          const userData = {
            email: data.user_email,
            name: data.user_name,
            session_token: data.session_token,
            role: 'admin'
          };
          const success = login(userData);
          if (!success) setError('Demo login failed — email not authorized.');
        } else {
          setError(data.detail === 'Incorrect access password' ? 'Incorrect access password.' : 'Demo login failed. Please try again.');
        }
      })
      .catch(() => setError('Demo login failed. Please try again.'))
      .finally(() => setLoading(false));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 mb-6">
            <img src="https://cdn.signaturehound.com/users/6ye5ponkqg39anj/1f6lmi6zopy8.png" alt="HOLONS CRM" className="w-20 h-20 object-contain" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">HOLONS CRM</h1>
          <p className="text-gray-600">Staff Access Portal</p>
          <div className="w-16 h-1 bg-gradient-to-r from-teal-400 to-blue-500 mx-auto mt-3 rounded-full"></div>
        </div>
        <div className="mb-6 text-center">
          <p className="text-gray-700 mb-4">Sign in with your HOLONS Google Workspace account</p>
        </div>
        <div className="flex flex-col items-center">
          <input
            type="password"
            value={demoPassword}
            onChange={e => setDemoPassword(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleDemoLogin(); }}
            placeholder="Demo access password"
            data-testid="demo-password-input"
            className="w-full max-w-[350px] mb-2 py-2.5 px-4 border-2 border-gray-200 rounded-lg text-sm text-center focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
          />
          <button
            onClick={handleDemoLogin}
            disabled={loading}
            data-testid="demo-login-button"
            className="w-full max-w-[350px] mb-4 py-3 px-6 bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700 text-white font-semibold rounded-lg shadow-md transition-all disabled:opacity-50"
          >
            {loading ? 'Signing you in...' : 'Staff Demo Login'}
          </button>
          <div className="flex items-center w-full max-w-[350px] mb-4">
            <div className="flex-1 border-t border-gray-200"></div>
            <span className="px-3 text-xs text-gray-400">or</span>
            <div className="flex-1 border-t border-gray-200"></div>
          </div>
          {!loading ? (
            <div id="google-signin-button" className="mb-4"></div>
          ) : (
            <div className="flex items-center justify-center py-3 px-6 mb-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mr-3"></div>
              <span className="text-gray-600">Signing you in...</span>
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 w-full">
              <p className="text-red-600 text-sm text-center">{error}</p>
            </div>
          )}
          <div className="text-center text-xs text-gray-500 mt-4">
            <p>Authorized for HOLONS staff only</p>
            <p className="mt-1">Need access? Contact your administrator</p>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-500">HOLONS Services</p>
          <p className="text-xs text-gray-400 mt-1">Secure Staff Portal • Protected by Google Workspace</p>
        </div>
      </div>
    </div>
  );
};

export default PureSoulsLogin;
